import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiArrowRight, FiHome } from 'react-icons/fi';

const LessonNavigation = ({ currentLessonId }) => {
  const navigate = useNavigate();

  // Define the lesson order and details
  const lessons = [
    {
      id: 'arithmetic-foundations',
      title: 'Arithmetic Foundations',
      path: '/exams/gre/concepts/arithmetic-foundations',
    },
    {
      id: 'math-translation',
      title: 'Math Translation',
      path: '/exams/gre/concepts/math-translation',
    },
    {
      id: 'math-shortcuts',
      title: 'Math Shortcuts & Mental Math',
      path: '/exams/gre/concepts/math-shortcuts',
    },
    {
      id: 'integers',
      title: 'Properties and Types of Integers',
      path: '/exams/gre/concepts/integers',
    },
    {
      id: 'percentage-ratio',
      title: 'Percentage & Ratio',
      path: '/exams/gre/concepts/percentage-ratio',
    },
    {
      id: 'exponents',
      title: 'Exponents & Roots',
      path: '/exams/gre/concepts/exponents',
    },
    { id: 'algebra', title: 'Algebra', path: '/exams/gre/concepts/algebra' },
    {
      id: 'sequences',
      title: 'Sequences',
      path: '/exams/gre/concepts/sequences',
    },
    {
      id: 'absolute-value',
      title: 'Absolute Value',
      path: '/exams/gre/concepts/absolute-value',
    },
    {
      id: 'functions',
      title: 'Functions',
      path: '/exams/gre/concepts/functions',
    },
    {
      id: 'sets',
      title: 'Sets & Venn Diagrams',
      path: '/exams/gre/concepts/sets',
    },
    {
      id: 'probabilities',
      title: 'Probabilities',
      path: '/exams/gre/concepts/probabilities',
    },
    {
      id: 'work-rate',
      title: 'Work & Rate Problems',
      path: '/exams/gre/concepts/work-rate',
    },
    {
      id: 'distance-rate',
      title: 'Distance, Rate & Time',
      path: '/exams/gre/concepts/distance-rate',
    },
    {
      id: 'coordinate-geometry',
      title: 'Coordinate Geometry',
      path: '/exams/gre/concepts/coordinate-geometry',
    },
    { id: 'circles', title: 'Circles', path: '/exams/gre/concepts/circles' },
    {
      id: 'triangles',
      title: 'Triangles',
      path: '/exams/gre/concepts/triangles',
    },
    {
      id: 'quadrilaterals',
      title: 'Quadrilaterals',
      path: '/exams/gre/concepts/quadrilaterals',
    },
    { id: 'polygons', title: 'Polygons', path: '/exams/gre/concepts/polygons' },
    {
      id: 'three-dimensional-figures',
      title: '3-Dimensional Figures',
      path: '/exams/gre/concepts/three-dimensional-figures',
    },
    {
      id: 'volumes',
      title: 'Volumes & Surface Area',
      path: '/exams/gre/concepts/volumes',
    },
    {
      id: 'statistics',
      title: 'Statistics: Mean, Median & Mode',
      path: '/exams/gre/concepts/statistics',
    },
    {
      id: 'graphs',
      title: 'Graphs & Data Interpretation',
      path: '/exams/gre/concepts/graphs',
    },
  ];

  const currentIndex = lessons.findIndex(
    (lesson) => lesson.id === currentLessonId
  );
  const previousLesson = currentIndex > 0 ? lessons[currentIndex - 1] : null;
  const nextLesson =
    currentIndex < lessons.length - 1 ? lessons[currentIndex + 1] : null;

  const handleNavigation = (path, activeCategory) => {
    if (activeCategory) {
      navigate(path, { state: { activeCategory: activeCategory } });
      return;
    }
    navigate(path, { replace: true });
  };

  return (
    <div
      className="gre-quant-lesson-nav"
      style={{
        marginTop: '3rem',
        padding: '2rem',
        background: 'var(--liwaza-surface-container)',
        borderRadius: '12px',
        border: '1px solid var(--liwaza-outline-variant)',
      }}
    >
      {/* Progress indicator */}
      <div style={{ marginBottom: '2rem' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '0.5rem',
          }}
        >
          <span
            style={{
              fontSize: '0.9rem',
              color: 'var(--liwaza-on-surface-variant)',
              fontWeight: 500,
            }}
          >
            Lesson Progress
          </span>
          <span
            style={{
              fontSize: '0.9rem',
              color: 'var(--liwaza-primary)',
              fontWeight: 600,
            }}
          >
            {currentIndex + 1} of {lessons.length}
          </span>
        </div>

        <div
          style={{
            width: '100%',
            height: '6px',
            background: 'var(--liwaza-surface-container-highest)',
            borderRadius: '3px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              width: `${((currentIndex + 1) / lessons.length) * 100}%`,
              height: '100%',
              background:
                'linear-gradient(90deg, var(--liwaza-primary), var(--liwaza-secondary))',
              borderRadius: '3px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      </div>

      {/* Navigation buttons */}
      <div
        className="gre-quant-lesson-nav__controls"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        {/* Previous lesson */}
        <div style={{ flex: 1 }}>
          {previousLesson ? (
            <button
              onClick={() => handleNavigation(previousLesson.path)}
              style={{
                background: 'none',
                border: '1px solid var(--liwaza-outline)',
                padding: '1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                width: '100%',
                textAlign: 'left',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background =
                  'var(--liwaza-surface-container-highest)';
                e.target.style.borderColor = 'var(--liwaza-primary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'none';
                e.target.style.borderColor = 'var(--liwaza-outline)';
              }}
            >
              <FiArrowLeft size={20} color="var(--liwaza-primary)" />
              <div>
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--liwaza-on-surface-variant)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Previous
                </div>
                <div
                  style={{
                    fontSize: '0.95rem',
                    color: 'var(--liwaza-on-surface)',
                    fontWeight: 500,
                    lineHeight: 1.3,
                  }}
                >
                  {previousLesson.title}
                </div>
              </div>
            </button>
          ) : (
            <div style={{ height: '72px' }} /> // Placeholder to maintain alignment
          )}
        </div>

        {/* Back to concepts */}
        <div
          className="gre-quant-lesson-nav__home"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <button
            onClick={() => handleNavigation('/exams/gre', 'concepts')}
            style={{
              background: 'var(--liwaza-surface-container-highest)',
              border: '1px solid var(--liwaza-outline)',
              padding: '1rem',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
              marginBottom: '0.5rem',
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'var(--liwaza-primary)';
              e.target.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.target.style.background =
                'var(--liwaza-surface-container-highest)';
              e.target.style.color = 'var(--liwaza-on-surface)';
            }}
            title="Back to Concepts Overview"
          >
            <FiHome size={20} />
          </button>
          <span
            style={{
              fontSize: '0.8rem',
              color: 'var(--liwaza-on-surface-variant)',
              textAlign: 'center',
            }}
          >
            All Concepts
          </span>
        </div>

        {/* Next lesson */}
        <div style={{ flex: 1 }}>
          {nextLesson ? (
            <button
              onClick={() => handleNavigation(nextLesson.path)}
              style={{
                background: 'none',
                border: '1px solid var(--liwaza-outline)',
                padding: '1rem',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                width: '100%',
                textAlign: 'right',
                justifyContent: 'flex-end',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background =
                  'var(--liwaza-surface-container-highest)';
                e.target.style.borderColor = 'var(--liwaza-primary)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'none';
                e.target.style.borderColor = 'var(--liwaza-outline)';
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: '0.8rem',
                    color: 'var(--liwaza-on-surface-variant)',
                    marginBottom: '0.25rem',
                  }}
                >
                  Next
                </div>
                <div
                  style={{
                    fontSize: '0.95rem',
                    color: 'var(--liwaza-on-surface)',
                    fontWeight: 500,
                    lineHeight: 1.3,
                  }}
                >
                  {nextLesson.title}
                </div>
              </div>
              <FiArrowRight size={20} color="var(--liwaza-primary)" />
            </button>
          ) : (
            <div
              style={{
                height: '72px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--liwaza-outline)',
                borderRadius: '8px',
                background: 'var(--liwaza-surface-container-highest)',
              }}
            >
              <span
                style={{
                  fontSize: '0.9rem',
                  color: 'var(--liwaza-on-surface-variant)',
                  fontWeight: 500,
                }}
              >
                🎉 Course Complete!
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Quick lesson overview */}
      <div
        style={{
          marginTop: '1.5rem',
          padding: '1rem',
          background: 'var(--liwaza-surface-container-highest)',
          borderRadius: '8px',
          border: '1px solid var(--liwaza-outline-variant)',
        }}
      >
        <h4
          style={{
            margin: '0 0 0.75rem 0',
            color: 'var(--liwaza-on-surface)',
            fontSize: '1rem',
          }}
        >
          Course Overview
        </h4>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '0.5rem',
          }}
        >
          {lessons.map((lesson, index) => (
            <button
              key={lesson.id}
              onClick={() => handleNavigation(lesson.path)}
              style={{
                background:
                  lesson.id === currentLessonId
                    ? 'var(--liwaza-primary)'
                    : 'none',
                border: `1px solid ${lesson.id === currentLessonId ? 'var(--liwaza-primary)' : 'var(--liwaza-outline-variant)'}`,
                color:
                  lesson.id === currentLessonId
                    ? 'white'
                    : 'var(--liwaza-on-surface)',
                padding: '0.5rem 0.75rem',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.85rem',
                textAlign: 'left',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (lesson.id !== currentLessonId) {
                  e.target.style.background = 'var(--liwaza-primary-container)';
                  e.target.style.borderColor = 'var(--liwaza-primary)';
                }
              }}
              onMouseLeave={(e) => {
                if (lesson.id !== currentLessonId) {
                  e.target.style.background = 'none';
                  e.target.style.borderColor = 'var(--liwaza-outline-variant)';
                }
              }}
            >
              <span style={{ fontWeight: 500 }}>{index + 1}.</span>{' '}
              {lesson.title}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LessonNavigation;
